import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface AuthWrapperProps {
  children: React.ReactNode;
}

export const AuthWrapper = ({ children }: AuthWrapperProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          await supabase.auth.signOut();
          navigate('/');
          return;
        }

        if (!session) {
          console.log('No session found, redirecting to auth');
          navigate('/auth');
          return;
        }

        // Only try to refresh if we have a valid session
        if (session) {
          const expiresAt = session?.expires_at ? session.expires_at * 1000 : 0; // Convert to milliseconds
          const now = Date.now();
          const timeUntilExpiry = expiresAt - now;

          // If session expires in less than 5 minutes or is expired, try to refresh
          if (timeUntilExpiry < 300000) {
            console.log('Session about to expire or expired, attempting refresh...');
            const { data, error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError) {
              console.error('Session refresh error:', refreshError);
              // If refresh fails, sign out and redirect
              await supabase.auth.signOut();
              toast.error('Session expired. Please sign in again.');
              navigate('/');
              return;
            }

            if (!data.session) {
              console.log('No new session after refresh, redirecting to auth');
              await supabase.auth.signOut();
              navigate('/auth');
              return;
            }
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Session check error:', error);
        await supabase.auth.signOut();
        toast.error('Authentication error. Please sign in again.');
        navigate('/');
      }
    };

    // Initial session check
    checkSession();

    // Set up auth state change listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session);
      
      if (event === 'SIGNED_OUT' || !session) {
        navigate('/');
      } else if (event === 'SIGNED_IN') {
        setIsLoading(false);
        navigate('/app');
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed successfully');
        setIsLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sage-600" />
      </div>
    );
  }

  return <>{children}</>;
};